import {
  get as _get,
  set as _set,
  clone as _clone,
  isEmpty as _isEmpty,
  isArray as _isArray,
  isObject as _isObject,
  isFunction as _isFunction,
  forEach as _forEach,
  snakeCase as _snakeCase,
  findIndex as _findIndex,
} from 'lodash';
import validator from './Validator';
import groupValidator from './groupValidator';

export default class Entity {
  key = 0;

  dataLoaded = false;
  metaLoaded = false;

  data = null;
  meta = null;
  errors = null;
  mainMetaIdentifier = null;

  processedData = {};
  isValid = true;
  errors = [];

  extraValidators = [];

  validators = validator;
  groupValidators = groupValidator;

  setData = data => {
    this.dataLoaded = true;
    this.data = _clone(data);

    if (this.metaLoaded) {
      this.processData();
    }

    return this;
  };

  getDataLoaded = () => {
    return this.dataLoaded;
  };

  // this is the whole meta information, not only for this entity
  // but we also need the main meta information to know where to start from
  setMeta = (meta, mainMetaIdentifier) => {
    this.metaLoaded = true;
    this.meta = meta;
    this.mainMetaIdentifier = mainMetaIdentifier;

    if (this.dataLoaded) {
      this.processData();
    }

    return this;
  };

  getMetaLoaded = () => {
    return this.metaLoaded;
  };

  // we have all the data and the meta information
  // so we can format the data structure to
  processData = () => {
    this.processedData = this.loopMeta(this.data, this.mainMetaIdentifier, []);
    this.key++;
  };

  getMetaIdentifier = (fieldName, fieldTree = []) => {
    if (fieldTree.length === 0) {
      return this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'MetaInformation']().type;
    }

    let toRead = this.processedData;
    for (let i = 0; i < fieldTree.length; i++) {
      toRead = toRead[fieldTree[i]];
    }
    return toRead['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'MetaInformation']().type;
  };

  createValue = (fieldName, fieldTree = [], initialValue = {}) => {
    fieldTree = _clone(fieldTree);
    const metaIdentifier = this.getMetaIdentifier(fieldName, fieldTree);

    fieldTree.push(fieldName);

    if (!_isEmpty(initialValue)) {
      this.updateOriginalData(fieldName, initialValue, fieldTree);
    }
    return this.loopMeta(initialValue, metaIdentifier, fieldTree, false);
  };

  // get a value and update it inside the entity
  updateValue = (fieldName, value, fieldTree = []) => {
    if (
      value === undefined ||
      value === null ||
      (_isEmpty(value) && value !== true && value !== false && value.constructor.toString().indexOf('Array') === -1)
    ) {
      const metaIdentifier = this.getMetaIdentifier(fieldName, _clone(fieldTree));
      if (metaIdentifier !== undefined && metaIdentifier.indexOf(':') !== -1) {
        value = this.createValue(fieldName, _clone(fieldTree));
      }
    }

    if (fieldTree.length === 0) {
      this.processedData[fieldName] = value;
      this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1)] = () => {
        return value;
      };
    } else {
      let toSet = this.processedData;
      for (let i = 0; i < fieldTree.length; i++) {
        toSet = toSet[fieldTree[i]];
      }
      toSet[fieldName] = value;

      toSet['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1)] = () => {
        return value;
      };
    }
    this.updateOriginalData(fieldName, value, fieldTree);

    this.key++;
    return this;
  };

  updateValidation = (fieldName, validationResult, fieldTree = []) => {
    if (!validationResult.valid) {
      this.errors.push({ fieldName, validationResult, fieldTree });
      this.isValid = false;
    }

    if (fieldTree.length === 0) {
      this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'ValidationResult'] = () => {
        return validationResult;
      };
    } else {
      let toSet = this.processedData;
      for (let i = 0; i < fieldTree.length; i++) {
        toSet = toSet[fieldTree[i]];
      }

      toSet['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'ValidationResult'] = () => {
        return validationResult;
      };
    }
  };

  loopMeta = (data, metaIdentifier, fieldTree = [], useThisWhenTreeEmpty = true) => {
    const result = {};
    if (!this.meta[metaIdentifier]) {
      return data;
    }

    const meta = this.meta[metaIdentifier].data;
    const metaKeys = Object.keys(meta);
    for (let i = 0; i < metaKeys.length; i++) {
      const fieldName = metaKeys[i];
      if (Object.prototype.hasOwnProperty.call(meta, fieldName)) {
        const fieldMeta = meta[fieldName];

        if (
          fieldMeta.complexType !== undefined &&
          fieldMeta.complexType &&
          fieldMeta.array !== undefined &&
          fieldMeta.array
        ) {
          // this field is a complex type and is an array
          const values = [];
          if (data && (data[fieldName] || data[fieldMeta.name]) !== undefined) {
            _forEach(data[fieldName] || data[fieldMeta.name], (value, key) => {
              const newFieldTree = fieldTree.slice();
              newFieldTree.push(fieldName);
              values[key] = this.loopMeta(
                value !== undefined ? value : {},
                fieldMeta.type,
                newFieldTree,
                useThisWhenTreeEmpty
              );
            });
          }
          result[fieldName] = values;
        } else if (fieldMeta.complexType !== undefined && fieldMeta.complexType) {
          // this field is complex type, but not an array
          const newFieldTree = fieldTree.slice();
          newFieldTree.push(fieldName);
          result[fieldName] = this.loopMeta(
            data && (data[fieldName] || data[fieldMeta.name]) !== undefined
              ? data[fieldName] || data[fieldMeta.name]
              : {},
            fieldMeta.type,
            newFieldTree,
            useThisWhenTreeEmpty
          );
        } else if (fieldMeta.array !== undefined && fieldMeta.array) {
          // this is an array of simple type data
          const values = [];
          if (data && (data[fieldName] || data[fieldMeta.name]) !== undefined) {
            _forEach(data[fieldName] || data[fieldMeta.name], (value, key) => {
              values[key] = value;
            });
          }
          result[fieldName] = values;
        } else if (data !== null && (data[fieldName] || data[fieldMeta.name]) !== undefined) {
          result[fieldName] = data[fieldName] || data[fieldMeta.name];
        } else {
          result[fieldName] = null;
        }

        if (fieldTree.length === 0 && useThisWhenTreeEmpty) {
          this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1)] = () => {
            return result[fieldName];
          };
          this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'MetaInformation'] = () => {
            return fieldMeta;
          };
          this['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'ValidationResult'] = () => {
            return { valid: true };
          };
        } else {
          result['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1)] = () => {
            return result[fieldName];
          };
          result['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'MetaInformation'] = () => {
            return fieldMeta;
          };
          result['get' + fieldName.charAt(0).toUpperCase() + fieldName.substr(1) + 'ValidationResult'] = () => {
            return { valid: true };
          };
        }
      }
    }

    return result;
  };

  validate = () => {
    this.isValid = true; // initial value; if we find some errors, the value will be false
    this.errors = [];
    this.loopValidate(this.processedData, this.mainMetaIdentifier, []);
    this.loopExtraValidators();

    this.key++;

    return this;
  };

  loopValidate = (data, metaIdentifier, fieldTree = []) => {
    const meta = this.meta[metaIdentifier].data;
    const metaKeys = Object.keys(meta);
    for (let i = 0; i < metaKeys.length; i++) {
      const fieldName = metaKeys[i];
      if (Object.prototype.hasOwnProperty.call(meta, fieldName)) {
        const fieldMeta = meta[fieldName];

        if (
          fieldMeta.complexType !== undefined &&
          fieldMeta.complexType &&
          fieldMeta.array !== undefined &&
          fieldMeta.array
        ) {
          // this field is a complex type and is an array
          if (
            data[fieldMeta.name] !== undefined &&
            Object.prototype.toString.call(data[fieldMeta.name]) === '[object Array]'
          ) {
            if (fieldMeta.ref === undefined || !fieldMeta.ref) {
              // it's not a referenced object, we need to validate it
              Object.keys(data[fieldMeta.name]).forEach(key => {
                const newFieldTree = fieldTree.slice();
                newFieldTree.push(fieldName);
                newFieldTree.push(key);
                this.loopValidate(data[fieldName][key], fieldMeta.type, newFieldTree);
              });
            } else if (fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
              // it's an array of referenced objects, but it is required. check if user has selected at least one
              if (data[fieldMeta.name] === undefined || data[fieldMeta.name].length === 0) {
                this.updateValidation(
                  fieldMeta.name,
                  { valid: false, rule: 'required', helpText: fieldMeta.validate.messages.required },
                  fieldTree
                );
              } else {
                this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
              }
            }
          }
        } else if (fieldMeta.complexType !== undefined && fieldMeta.complexType) {
          // this field is complex type, but not an array
          if (fieldMeta.ref === undefined || !fieldMeta.ref) {
            if (
              (fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) ||
              (data[fieldName] && data[fieldName].id)
            ) {
              const originalValue = this.getOriginalValueByPath([...fieldTree, fieldName]);
              if (originalValue) {
                // it's not a referenced object, we need to validate it
                const newFieldTree = fieldTree.slice();
                newFieldTree.push(fieldName);
                this.loopValidate(data[fieldName] !== undefined ? data[fieldName] : {}, fieldMeta.type, newFieldTree);
              }
              const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
              const validate = this.validateOne(originalValue, fieldRules, fieldMeta.validate.messages);
              if (!validate.valid) {
                this.updateValidation(fieldName, validate, fieldTree);
              } else {
                this.updateValidation(fieldName, { valid: true }, fieldTree);
              }
            } else {
              this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
            }
          } else if (fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
            // it's a referenced object, but it is required. check if user has set an object
            let hasID = false;
            if (fieldMeta.type === 'Client:Client') {
              hasID = !!(data[fieldName] !== undefined && data[fieldName].username);
            } else {
              hasID = !!(data[fieldName] !== undefined && data[fieldName].id);
            }

            // check if the group requirement does take place
            const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
            if (_findIndex(fieldRules, ['name', 'required']) === -1) {
              hasID = true;
            }

            if (!hasID) {
              this.updateValidation(
                fieldMeta.name,
                { valid: false, rule: 'required', helpText: fieldMeta.validate.messages.required },
                fieldTree
              );
            } else {
              this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
            }
          }
        } else if (fieldMeta.array !== undefined && fieldMeta.array) {
          // this is an array of simple type data
          if (data[fieldName] !== undefined && Object.prototype.toString.call(data[fieldName]) === '[object Array]') {
            if (fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
              const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
              let arrayValid = true;
              Object.keys(data[fieldName]).forEach(key => {
                // data[ fieldMeta.name ][key]
                const validate = this.validateOne(data[fieldName][key], fieldRules, fieldMeta.validate.messages);

                const newFieldTree = fieldTree.slice();
                if (fieldMeta.complexType) {
                  newFieldTree.push(key);
                }

                if (!validate.valid) {
                  this.updateValidation(fieldName, validate, newFieldTree);
                  arrayValid = false;
                } else {
                  this.updateValidation(fieldName, { valid: true }, newFieldTree);
                }
              });

              this.updateValidation(fieldName, { valid: arrayValid }, fieldTree);
            }
          }
        }
        // data[ fieldMeta.name ] !== undefined ? data[ fieldMeta.name ] : null;
        // validate only if the field is required or if a value is set
        else if (
          fieldMeta.validate.rules &&
          (fieldMeta.validate.rules.indexOf('required') !== -1 || (data[fieldName] !== undefined && data[fieldName]))
        ) {
          const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
          const validate = this.validateOne(data[fieldName], fieldRules, fieldMeta.validate.messages);

          if (!validate.valid) {
            this.updateValidation(fieldName, validate, fieldTree);
          } else {
            this.updateValidation(fieldName, { valid: true }, fieldTree);
          }
        }
      }
    }
  };

  getOriginalValueByPath = pathArray => {
    const originalPath = pathArray.map(item => _snakeCase(item));

    return _get(this.data, originalPath);
  };

  updateOriginalData = (fieldName, value, fieldTree) => {
    const originalFieldName = _snakeCase(fieldName);
    const originalPath = fieldTree.map(_snakeCase);
    const originalValue = this.convertToOriginalValue(value);

    _set(this.data, [...originalPath, originalFieldName], originalValue);
  };

  convertToOriginalValue = value => {
    if (_isArray(value)) {
      return value.map(val => this.convertToOriginalValue(val));
    }

    if (_isObject(value)) {
      return Object.keys(value)
        .filter(key => !_isFunction(value[key]))
        .reduce((original, key) => {
          original[_snakeCase(key)] = this.convertToOriginalValue(value[key]);
          return original;
        }, {});
    }
    return value;
  };

  getValidationRules = (validateConfig, groupMap = {}, data) => {
    if (!validateConfig) {
      return [];
    }

    return validateConfig
      .split(',')
      .map(rule => {
        const params = rule.split(':');
        let name = params.shift();
        const inverse = name[0] === '!';

        if (inverse) {
          name = name.substr(1);
        }

        return { name, inverse, params };
      })
      .filter(({ name }) => this.validateGroup(groupMap[name], data));
  };

  validateGroup = (groups, data) => {
    return (groups || [])
      .filter(groupName => this.groupValidators[groupName])
      .every(groupName => this.groupValidators[groupName](data, this.processedData));
  };

  validateOne = (value, rules, helpText) => {
    const result = { valid: true };
    rules.forEach(rule => {
      if (typeof this.validators[rule.name] !== 'function') {
        throw new Error('Invalid input validation rule "' + rule.name + '"');
      }

      let ruleResult = this.validators[rule.name](value, ...rule.params);

      if (rule.inverse) {
        ruleResult = !ruleResult;
      }

      if (result.valid && ruleResult !== true) {
        result.valid = false;
        result.rule = rule.name;
        result.helpText = helpText[rule.name];
      }
    });

    return result;
  };

  // there is a possibility to set own validators
  // for example when the validator uses not only one field
  addValidator = validator => {
    this.extraValidators.push(validator);
  };

  resetValidators = () => {
    this.extraValidators = [];
  };

  // check the extra validators (if there are any)
  loopExtraValidators = () => {
    _forEach(this.extraValidators, validateFunction => {
      const result = validateFunction(this);
      if (!result.valid) {
        this.isValid = false;
        _forEach(result.fields, field => {
          this.updateValidation(field.name, result, field.fieldTree);
        });
      }
    });
  };

  // copy object and skip id's, so that there won't be any conflicts in the db
  copyObject = obj => {
    const result = {};

    const objKeys = Object.keys(obj);
    for (let i = 0; i < objKeys.length; i++) {
      const fieldName = objKeys[i];

      if (
        Object.prototype.hasOwnProperty.call(obj, fieldName) &&
        fieldName !== 'id' &&
        fieldName !== 'username' &&
        Object.prototype.toString.call(obj[fieldName]) !== '[object Function]'
      ) {
        const fieldNameUnderscore = fieldName
          .replace(/\.?([A-Z]+)/g, (x, y) => {
            return '_' + y.toLowerCase();
          })
          .replace(/^_/, '');

        if (Object.prototype.toString.call(obj[fieldName]) === '[object Array]') {
          const array = [];
          Object.keys(obj[fieldName]).forEach(key => {
            array.push(this.copyObject(obj[fieldName][key]));
          });
          result[fieldNameUnderscore] = array;
        } else if (obj[fieldName] !== null && typeof obj[fieldName] === 'object') {
          result[fieldNameUnderscore] = this.copyObject(obj[fieldName]);
        } else {
          result[fieldNameUnderscore] = obj[fieldName];
        }
      }
    }

    return result;
  };
}
